// Money is integer micro-dollars everywhere in the RPC API (1 USD = 1,000,000).

export const MICROS_PER_USD = 1_000_000;

/** Whole cents, e.g. "$1,234.50". Sub-cent amounts keep their digits ("$0.00025"). */
export function usd(micros: number | null | undefined): string {
  if (micros == null) return '—';
  const sign = micros < 0 ? '-' : '';
  const abs = Math.abs(micros);
  if (abs !== 0 && abs < 10_000) {
    // Below one cent: show up to six decimals, trimmed (250 → $0.00025).
    const s = (abs / MICROS_PER_USD).toFixed(6).replace(/0+$/, '');
    return `${sign}$${s.endsWith('.') ? s + '00' : s}`;
  }
  const dollars = abs / MICROS_PER_USD;
  return `${sign}$${dollars.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** A list price, exactly: whole dollars when it's whole, else the fewest decimals
 *  (at least two) that show all of it. $49, $1.20, $0.006, $0.015, $12.345. */
export function price(micros: number): string {
  const sign = micros < 0 ? '-' : '';
  const [whole, decimals] = (Math.abs(micros) / MICROS_PER_USD)
    .toFixed(6)
    .replace(/\.?0+$/, '')
    .split('.');
  const dollars = Number(whole).toLocaleString('en-US');
  return `${sign}$${dollars}${decimals ? `.${decimals.padEnd(2, '0')}` : ''}`;
}

/** A per-block price, exactly, with at least three decimals so a column of them
 *  lines up: $0.020, $0.006, $0.00025. */
export function pricePerBlock(micros: number): string {
  const [whole, decimals = ''] = price(micros).split('.');
  return `${whole}.${decimals.padEnd(3, '0')}`;
}

/** Signed amount for ledger rows: "+$10.00" / "-$0.12". */
export function signedUsd(micros: number): string {
  if (micros > 0) return `+${usd(micros)}`;
  return usd(micros);
}

/** 10000000 → "10M", 1500 → "1.5K", 999 → "999". */
export function compact(n: number): string {
  const abs = Math.abs(n);
  const fmt = (v: number, suffix: string) =>
    `${Number.isInteger(v) ? v : v.toFixed(v >= 10 ? 0 : 1).replace(/\.0$/, '')}${suffix}`;
  if (abs >= 1_000_000_000) return fmt(n / 1_000_000_000, 'B');
  if (abs >= 1_000_000) return fmt(n / 1_000_000, 'M');
  if (abs >= 1_000) return fmt(n / 1_000, 'K');
  return String(n);
}

export function count(n: number): string {
  return n.toLocaleString('en-US');
}

/** Dollars typed by a person → micros, or null when it isn't a valid amount. */
export function parseDollars(raw: string): number | null {
  const s = raw.trim().replace(/^\$/, '').replace(/,/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  return Math.round(Number(s) * 100) * 10_000;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  // Billing periods and usage days are UTC, so dates are shown in UTC.
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
